import { client as apiClient } from '../api/client';
import { Category } from '../models/Category';
import { Video, CoachingTip, Tag, SearchQuery } from '../models';

export interface ApiVideo extends Partial<Video> {
  categoryId: number;
  title?: string;
  url?: string;
  thumbnail?: string;
  previousVideo?: ApiVideo;
  nextVideo?: ApiVideo;
  tag: Array<Tag>;
}

export class CategoryStub extends Category {
  constructor(id: number) {
    super(id, '');
  }
}

export interface ApiCoachingTip extends Partial<CoachingTip> {}

export interface VideoRepository {
  fetchForCategory(category: Category): Promise<Array<Video>>;

  fetchVideo(
    id: number,
    category?: Category,
    favourite?: boolean,
    featured?: boolean,
    recentlyAdded?: boolean
  ): Promise<Video>;

  fetchFeatured(): Promise<Array<Video>>;

  fetchRecentlyAdded(): Promise<Array<Video>>;

  fetchFavourites(): Promise<Array<Video>>;

  fetchSearchVideos(query: SearchQuery): Promise<Array<Video>>;

  saveVideoEvent(
    video: Video,
    eventName: string,
    timestamp: string
  ): Promise<void>;

  favouriteVideo(video: Video): Promise<void>;

  unfavouriteVideo(video: Video): Promise<void>;

  likeVideo(video: Video): Promise<void>;
}

export class FetchVideoRepository implements VideoRepository {
  public async fetchForCategory(category: Category): Promise<Array<Video>> {
    return apiClient.get<Video, ApiVideo>(
      `videos?category=${category.id}`,
      videoItem => this.buildVideo(videoItem, category)
    );
  }

  public async fetchSearchVideos(query: SearchQuery): Promise<Array<Video>> {
    let searchUrl = `videos?`;

    searchUrl += `tags=${query.tags.map(tag => tag.id).join()}`;

    return apiClient.get<Video, ApiVideo>(searchUrl, videoItem =>
      this.buildVideo(videoItem)
    );
  }

  public async fetchVideo(
    id: number,
    category?: Category,
    favourite?: boolean,
    featured?: boolean,
    recentlyAdded?: boolean
  ): Promise<Video> {
    // TODO: Handle fetching related videos (separate endpoint?)
    const videos = await apiClient.get<Video, ApiVideo>(
      `videos/${id}`,
      async data =>
        this.buildDetailsLoadedVideo(
          data,
          category,
          favourite,
          featured,
          recentlyAdded
        )
    );

    return videos && videos[0];
  }

  public async fetchFeatured(): Promise<Array<Video>> {
    return apiClient.get<Video>(`videos/featured`, async data =>
      this.fetchVideo(data.id || -1, undefined, undefined, true)
    );
  }

  public async fetchRecentlyAdded(): Promise<Array<Video>> {
    return apiClient.get<Video>(`videos/recent`, async data =>
      this.fetchVideo(data.id || -1, undefined, undefined, undefined, true)
    );
  }

  public async fetchFavourites(): Promise<Array<Video>> {
    return apiClient.get<Video>(`user/favourites`, async data =>
      this.fetchVideo(data.id || -1, undefined, true)
    );
  }

  public async saveVideoEvent(
    video: Video,
    eventName: string,
    timestamp: string
  ): Promise<void> {
    try {
      await apiClient.post(`events/video/${video.id}`, {
        name: eventName,
        timestamp
      });
    } catch (ex) {
      // Ignore event save failures
    }
  }

  public async favouriteVideo(video: Video): Promise<void> {
    await apiClient.put(`videos/${video.id}/favourite`, {});
  }

  public async unfavouriteVideo(video: Video): Promise<void> {
    await apiClient.delete(`videos/${video.id}/favourite`, {});
  }

  public async likeVideo(video: Video): Promise<void> {
    await apiClient.put(`videos/${video.id}/like`, {});
  }

  private buildDetailsLoadedVideo = async (
    data: ApiVideo,
    category?: Category,
    favourite?: boolean,
    featured?: boolean,
    recentlyAdded?: boolean
  ) => {
    return this.buildVideo(
      data,
      category,
      favourite,
      featured,
      recentlyAdded,
      true
    );
  };

  private buildVideo = async (
    data: ApiVideo,
    category?: Category,
    favourite?: boolean,
    featured?: boolean,
    recentlyAdded?: boolean,
    detailsLoaded: boolean = false
  ): Promise<Video> => {
    return new Video(
      data.id as number,
      data.title,
      data.url,
      data.thumbnail,
      data.description,
      category ? category : new CategoryStub(data.categoryId),
      data.duration,
      data.publishedAt,
      data.views,
      data.likes,
      data.favourite === undefined ? favourite : data.favourite,
      data.previousVideo
        ? await this.buildVideo(data.previousVideo)
        : undefined,
      data.nextVideo ? await this.buildVideo(data.nextVideo) : undefined,
      data.attachment,
      data.coachingInformation,
      data.coachingTips ? await this.buildTips(data.coachingTips) : undefined,
      // TODO: Handle fetching related videos (separate endpoint)
      undefined,
      detailsLoaded,
      data.tags,
      featured,
      recentlyAdded,
      data.type ? data.type : undefined
    );
  };

  private buildTips = (data: Array<ApiCoachingTip>): Array<CoachingTip> => {
    return data.map(tipData => new CoachingTip(tipData.text, tipData.image));
  };
}

let RepositoryClass: ImportedModule<Constructable<VideoRepository>>;
if (process.env.REACT_APP_NO_BACKEND === 'true') {
  RepositoryClass = require('./VideoMock');
} else {
  RepositoryClass = { default: FetchVideoRepository };
}

let repository: VideoRepository;
export default function getRepository() {
  if (!repository) {
    repository = new RepositoryClass.default();
  }

  return repository;
}



// WEBPACK FOOTER //
// ./src/app/repositories/Video.ts